import type {
  InjectableSnapshot,
  PlaceholderResource,
} from "../types/injectables";
import type { FeedItem, FeedItems } from "../types/content/feedItem";

import { InjectableHandler } from "./injectablesHandler/injectablesHandler";
import { isFeedItem } from "./typechecks";

const addInjectablesToFeed = (
  feedData: FeedItems,
  injectables: InjectableSnapshot,
): [FeedItems, InjectableSnapshot | null] => {
  const injectableHandler = new InjectableHandler({
    injectablesResources: injectables.resources,
    injectableSlotsList: injectables.slots,
    lastInjectedAtIndex: injectables.lastInjectedAtIndex,
    feedName: injectables.feedName,
    initialFeedWeight: injectables.initialFeedWeight,
    initialFeedWeightApplied: injectables.initialFeedWeightApplied,
    currentResource: injectables.currentResource,
    user: injectables.user,
  });

  let allSlotsAreUsed = injectableHandler.getIsAllSlotsUsed();
  let injectablePosition = 0;
  let spaceing = injectables.accumulatedWeightRest
    ? injectables.accumulatedWeightRest + 0
    : 0;
  let accumulatedWeightRest = 0;

  if (allSlotsAreUsed) {
    return [feedData, null];
  }

  const feedDataWithInjectables: FeedItems = feedData.reduce(
    (groups: FeedItems, group: FeedItem | PlaceholderResource, i) => {
      const noneInjected = [...groups, group];

      if (allSlotsAreUsed || !isFeedItem(group)) {
        return noneInjected;
      }

      const groupWeight = group.weight;

      if (groupWeight) {
        const injectableResource = injectableHandler.getAvailableResource();

        if (!injectableResource) {
          allSlotsAreUsed = injectableHandler.getIsAllSlotsUsed();
          if (allSlotsAreUsed) {
            spaceing = 0;
            return noneInjected;
          }
        }
        const feedDensityScoreIsApplied =
          injectableHandler.getInitialFeedWeightApplied();
        const currentDensityScore = injectableHandler.getCurrentDensityScore();
        const initalOffsetForFeed = injectableHandler.getInitalOffsetForFeed();

        const densityScore = feedDensityScoreIsApplied
          ? currentDensityScore
          : currentDensityScore + initalOffsetForFeed;

        if (densityScore > 0) {
          spaceing = spaceing + groupWeight;
        }

        if (spaceing >= densityScore) {
          if (!feedDensityScoreIsApplied) {
            injectableHandler.setInitialFeedWeightApplied(true);
          }

          if (!injectableResource) {
            spaceing = 0;
            const lastInjectedAtIndex = noneInjected.length - 1;

            injectableHandler.setLastInjectedAtIndex(lastInjectedAtIndex);
            injectableHandler.nextPosition();

            return noneInjected;
          }

          injectableHandler.nextPosition();
          injectableHandler.setCurrentPositionIsUsed(false);

          spaceing = 0;
          injectablePosition = injectablePosition + 1;

          const injectableResourceGroup = injectableResource;

          let feedGroupsWithInjectable = [
            ...groups,
            group,
            injectableResourceGroup,
          ];

          let lastInjectableIndexSubtrahend = 1;

          if (densityScore === 0) {
            const nextInjectableResourceGroup =
              injectableHandler.getAvailableResource();
            const nextCurrentDensityScore =
              injectableHandler.getCurrentDensityScore();

            feedGroupsWithInjectable = [
              ...groups,
              injectableResourceGroup,
              group,
            ];

            lastInjectableIndexSubtrahend = 2;
            spaceing = groupWeight;

            const isEligibleForNextInjection =
              groupWeight >= nextCurrentDensityScore &&
              nextInjectableResourceGroup;

            if (isEligibleForNextInjection) {
              feedGroupsWithInjectable = [
                ...groups,
                injectableResourceGroup,
                group,
                nextInjectableResourceGroup,
              ];

              lastInjectableIndexSubtrahend = 1;

              spaceing = 0;

              injectableHandler.nextPosition();
              injectableHandler.setCurrentPositionIsUsed(false);
            }
          }

          const lastInjectedAtIndex =
            feedGroupsWithInjectable.length - lastInjectableIndexSubtrahend;

          injectableHandler.setLastInjectedAtIndex(lastInjectedAtIndex);

          return feedGroupsWithInjectable;
        }
      }

      return noneInjected;
    },
    [],
  );

  const lastInjectedAtIndex = injectableHandler.getLastInjectedAtIndex();

  if (lastInjectedAtIndex < feedDataWithInjectables.length - 1) {
    feedDataWithInjectables
      .filter((_, i) => i > lastInjectedAtIndex)
      .forEach((group) => {
        if (isFeedItem(group)) {
          const groupWeight = group.weight;
          if (groupWeight) {
            accumulatedWeightRest = accumulatedWeightRest + groupWeight;
          }
        }
      });
  }

  const snapshot = injectableHandler.getInjectablesSnapshot();
  return [feedDataWithInjectables, { ...snapshot, accumulatedWeightRest }];
};

export { addInjectablesToFeed };
